import React, { useState } from 'react';
import { CheckCircleIcon, AlertTriangleIcon, ClockIcon, ArrowLeftIcon, FileTextIcon, CalendarIcon, CheckIcon, XIcon, SendIcon, CreditCardIcon, ShieldIcon, AlertCircleIcon } from 'lucide-react';
import { FileUpload } from '../FileUpload';
export function ComplianceCheck({
  vendor,
  onUpdate,
  setCurrentView,
  locale = 'en-US'
}) {
  const [selectedVendor, setSelectedVendor] = useState(vendor || null);
  const [files, setFiles] = useState([]);
  const [message, setMessage] = useState('');
  // If no vendor is selected, show a list of vendors with compliance issues
  const vendorsWithIssues = [{
    id: 'VEN-2023-002',
    name: 'Dell Technologies',
    status: 'Active',
    complianceStatus: 'Pending Review',
    type: 'IT Equipment',
    country: 'United States',
    onboardingDate: '2023-02-10',
    lastUpdated: '2023-10-05',
    issue: 'Insurance Certificate expired on 2023-10-10',
    documents: [{
      id: 1,
      name: 'Insurance Certificate',
      type: 'Insurance',
      uploadDate: '2023-02-10',
      expiryDate: '2023-10-10',
      status: 'Expired'
    }],
    bankVerification: {
      status: 'Verified',
      lastChecked: '2023-09-20',
      issues: []
    }
  }, {
    id: 'VEN-2023-003',
    name: 'PrintWorks Inc.',
    status: 'Pending Onboarding',
    complianceStatus: 'Incomplete',
    type: 'Printing Services',
    country: 'Canada',
    onboardingDate: '2023-09-30',
    lastUpdated: '2023-09-30',
    issue: 'Missing required documents',
    documents: [],
    bankVerification: {
      status: 'Not Verified',
      lastChecked: null,
      issues: [{
        severity: 'High',
        description: 'Bank account information not provided'
      }]
    }
  }, {
    id: 'VEN-2023-006',
    name: 'Global Supplies Ltd',
    status: 'Active',
    complianceStatus: 'Non-Compliant',
    type: 'Office Supplies',
    country: 'United Kingdom',
    onboardingDate: '2023-04-15',
    lastUpdated: '2023-10-18',
    issue: "Bank details don't match Eftsure verification",
    documents: [{
      id: 1,
      name: 'Insurance Certificate',
      type: 'Insurance',
      uploadDate: '2023-04-15',
      expiryDate: '2024-04-15',
      status: 'Valid'
    }],
    bankVerification: {
      status: 'Failed',
      lastChecked: '2023-10-18',
      issues: [{
        severity: 'Critical',
        description: "Account name doesn't match registered business name"
      }, {
        severity: 'High',
        description: "BSB/Routing number doesn't match registered bank"
      }]
    }
  }, {
    id: 'VEN-2023-007',
    name: 'TechCore Systems',
    status: 'Active',
    complianceStatus: 'Non-Compliant',
    type: 'IT Services',
    country: 'Australia',
    onboardingDate: '2023-05-22',
    lastUpdated: '2023-10-15',
    issue: 'Multiple compliance issues including bank verification',
    documents: [{
      id: 1,
      name: 'Business Registration',
      type: 'Legal',
      uploadDate: '2023-05-22',
      expiryDate: null,
      status: 'Valid'
    }, {
      id: 2,
      name: 'Insurance Certificate',
      type: 'Insurance',
      uploadDate: '2023-05-22',
      expiryDate: '2023-09-22',
      status: 'Expired'
    }],
    bankVerification: {
      status: 'Warning',
      lastChecked: '2023-10-15',
      issues: [{
        severity: 'Medium',
        description: 'Account details partially match - manual verification required'
      }]
    }
  }, {
    id: 'VEN-2023-008',
    name: 'Stellar Logistics',
    status: 'Active',
    complianceStatus: 'Non-Compliant',
    type: 'Logistics',
    country: 'United States',
    onboardingDate: '2023-03-10',
    lastUpdated: '2023-10-10',
    issue: 'Sanctions list match found',
    documents: [{
      id: 1,
      name: 'Business License',
      type: 'Legal',
      uploadDate: '2023-03-10',
      expiryDate: '2024-03-10',
      status: 'Valid'
    }],
    bankVerification: {
      status: 'Verified',
      lastChecked: '2023-10-10',
      issues: []
    },
    sanctionsCheck: {
      status: 'Failed',
      lastChecked: '2023-10-10',
      issues: [{
        severity: 'Critical',
        description: 'Potential match found on international sanctions list'
      }]
    }
  }];
  const handleFileUpload = newFiles => {
    setFiles([...files, ...newFiles]);
  };
  const removeFile = index => {
    setFiles(files.filter((_, i) => i !== index));
  };
  const handleUpdateStatus = status => {
    if (!selectedVendor) return;
    // Process new documents if any
    const newDocuments = files.map((file, index) => ({
      id: selectedVendor.documents.length + index + 1,
      name: file.name,
      type: 'Compliance Document',
      uploadDate: new Date().toISOString().split('T')[0],
      expiryDate: null,
      status: 'Valid'
    }));
    // Update vendor with new compliance status and documents
    const updatedVendor = {
      ...selectedVendor,
      complianceStatus: status,
      documents: [...selectedVendor.documents, ...newDocuments],
      lastUpdated: new Date().toISOString().split('T')[0]
    };
    onUpdate(updatedVendor);
    setCurrentView('vendors');
  };
  const handleSendReminder = () => {
    // In a real app, this would send an email to the vendor
    alert(`Reminder sent to ${selectedVendor.name} to update their compliance documents.`);
  };
  const getComplianceBadge = status => {
    switch (status) {
      case 'Compliant':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-21430">
            <CheckCircleIcon className="mr-1 h-3 w-3" data-id="element-21431" />
            {status}
          </span>;
      case 'Pending Review':
      case 'Incomplete':
      case 'Warning':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-21432">
            <ClockIcon className="mr-1 h-3 w-3" data-id="element-21433" />
            {status}
          </span>;
      case 'Non-Compliant':
      case 'Expired':
      case 'Failed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-21434">
            <AlertTriangleIcon className="mr-1 h-3 w-3" data-id="element-21435" />
            {status}
          </span>;
      case 'Verified':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-21436">
            <CheckCircleIcon className="mr-1 h-3 w-3" data-id="element-21437" />
            {status}
          </span>;
      case 'Not Verified':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-21438">
            <ClockIcon className="mr-1 h-3 w-3" data-id="element-21439" />
            {status}
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-21440">
            {status}
          </span>;
    }
  };
  const getDocumentStatusBadge = status => {
    switch (status) {
      case 'Valid':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-21441">
            <CheckCircleIcon className="mr-1 h-3 w-3" data-id="element-21442" />
            {status}
          </span>;
      case 'Pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-21443">
            <ClockIcon className="mr-1 h-3 w-3" data-id="element-21444" />
            {status}
          </span>;
      case 'Expired':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-21445">
            <AlertTriangleIcon className="mr-1 h-3 w-3" data-id="element-21446" />
            {status}
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-21447">
            {status}
          </span>;
    }
  };
  const getIssueIcon = issue => {
    if (issue.description.includes('bank') || issue.description.includes('account')) {
      return <CreditCardIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5 text-orange-500" data-id="element-21448" />;
    } else if (issue.description.includes('sanctions') || issue.description.includes('background')) {
      return <AlertCircleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5 text-red-500" data-id="element-21449" />;
    } else if (issue.description.includes('expired')) {
      return <ClockIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5 text-yellow-500" data-id="element-21450" />;
    } else {
      return <AlertTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5 text-red-500" data-id="element-21451" />;
    }
  };
  const getSeverityClass = severity => {
    switch (severity) {
      case 'Critical':
        return 'text-red-600';
      case 'High':
        return 'text-orange-600';
      case 'Medium':
        return 'text-yellow-600';
      case 'Low':
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  };
  if (!selectedVendor) {
    return <div data-id="element-21452">
        <div className="mb-8 flex items-center" data-id="element-21453">
          <button onClick={() => setCurrentView('dashboard')} className="mr-4 text-gray-400 hover:text-gray-500" data-id="element-21454">
            <ArrowLeftIcon className="h-5 w-5" data-id="element-21455" />
          </button>
          <div data-id="element-21456">
            <h3 className="text-xl font-semibold text-gray-900" data-id="element-21457">
              Compliance Issues
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-21458">
              Vendors requiring compliance review or updates
            </p>
          </div>
        </div>
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-21459">
          <ul className="divide-y divide-gray-200" data-id="element-21460">
            {vendorsWithIssues.length === 0 ? <li className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-21461">
                No compliance issues found.
              </li> : vendorsWithIssues.map(vendor => <li key={vendor.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedVendor(vendor)} data-id="element-21462">
                  <div className="flex items-center justify-between" data-id="element-21463">
                    <div className="flex items-center" data-id="element-21464">
                      <div className="flex-shrink-0" data-id="element-21465">
                        {vendor.bankVerification && vendor.bankVerification.status === 'Failed' ? <CreditCardIcon className="h-5 w-5 text-orange-400" data-id="element-21466" /> : vendor.sanctionsCheck && vendor.sanctionsCheck.status === 'Failed' ? <ShieldIcon className="h-5 w-5 text-red-500" data-id="element-21467" /> : <AlertTriangleIcon className="h-5 w-5 text-yellow-400" data-id="element-21468" />}
                      </div>
                      <p className="ml-2 text-sm font-medium text-indigo-600 truncate" data-id="element-21469">
                        {vendor.name}
                      </p>
                    </div>
                    <div className="ml-2 flex-shrink-0" data-id="element-21470">
                      {getComplianceBadge(vendor.complianceStatus)}
                    </div>
                  </div>
                  <div className="mt-2 sm:flex sm:justify-between" data-id="element-21471">
                    <div className="sm:flex" data-id="element-21472">
                      <p className="flex items-center text-sm text-gray-500" data-id="element-21473">
                        {vendor.issue}
                      </p>
                    </div>
                    <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0" data-id="element-21474">
                      <p data-id="element-21475">
                        Last updated:{' '}
                        <span className="ml-1.5" data-id="element-21476">{vendor.lastUpdated}</span>
                      </p>
                    </div>
                  </div>
                </li>)}
          </ul>
        </div>
      </div>;
  }
  return <div data-id="element-21477">
      <div className="mb-8 flex items-center" data-id="element-21478">
        <button onClick={() => setSelectedVendor(null)} className="mr-4 text-gray-400 hover:text-gray-500" data-id="element-21479">
          <ArrowLeftIcon className="h-5 w-5" data-id="element-21480" />
        </button>
        <div data-id="element-21481">
          <h3 className="text-xl font-semibold text-gray-900" data-id="element-21482">
            Compliance Review: {selectedVendor.name}
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-21483">
            {selectedVendor.id} • Current Status:{' '}
            {getComplianceBadge(selectedVendor.complianceStatus)}
          </p>
        </div>
      </div>
      {/* Compliance Issues */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-21484">
        <div className="px-4 py-5 sm:px-6" data-id="element-21485">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-21486">
            Compliance Issues
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-21487">
            Issues that need to be addressed
          </p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5" data-id="element-21488">
          {selectedVendor.complianceStatus === 'Compliant' && (!selectedVendor.bankVerification || selectedVendor.bankVerification.status === 'Verified') && (!selectedVendor.sanctionsCheck || selectedVendor.sanctionsCheck.status !== 'Failed') ? <div className="text-sm text-green-600 flex items-center" data-id="element-21489">
              <CheckCircleIcon className="h-5 w-5 mr-2" data-id="element-21490" />
              This vendor is currently compliant with all requirements.
            </div> : <div className="space-y-4" data-id="element-21491">
              {/* Document issues */}
              {selectedVendor.documents && selectedVendor.documents.some(doc => doc.status === 'Expired') && <div className="text-sm text-red-600 flex items-start" data-id="element-21492">
                    <AlertTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-21493" />
                    <div data-id="element-21494">
                      <p className="font-medium" data-id="element-21495">Expired Documents:</p>
                      <ul className="list-disc list-inside ml-4 mt-1" data-id="element-21496">
                        {selectedVendor.documents.filter(doc => doc.status === 'Expired').map(doc => <li key={doc.id} data-id="element-21497">
                              {doc.name} (Expired on {doc.expiryDate})
                            </li>)}
                      </ul>
                    </div>
                  </div>}
              {/* Missing documents */}
              {(!selectedVendor.documents || selectedVendor.documents.length === 0) && <div className="text-sm text-red-600 flex items-start" data-id="element-21498">
                  <AlertTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-21499" />
                  <div data-id="element-21500">
                    <p className="font-medium" data-id="element-21501">Missing Required Documents:</p>
                    <p className="mt-1" data-id="element-21502">
                      This vendor has not uploaded any required documents.
                    </p>
                  </div>
                </div>}
              {/* Bank verification issues */}
              {selectedVendor.bankVerification && selectedVendor.bankVerification.issues && selectedVendor.bankVerification.issues.length > 0 && <div className="text-sm text-orange-600 flex items-start" data-id="element-21503">
                    <CreditCardIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-21504" />
                    <div data-id="element-21505">
                      <p className="font-medium" data-id="element-21506">Bank Verification Issues:</p>
                      <ul className="list-disc list-inside ml-4 mt-1" data-id="element-21507">
                        {selectedVendor.bankVerification.issues.map((issue, index) => <li key={index} className={getSeverityClass(issue.severity)} data-id="element-21508">
                              {issue.description} ({issue.severity} severity)
                            </li>)}
                      </ul>
                      {selectedVendor.bankVerification.lastChecked && <p className="mt-1 text-xs text-gray-500" data-id="element-21509">
                          Last checked:{' '}
                          {selectedVendor.bankVerification.lastChecked}
                        </p>}
                    </div>
                  </div>}
              {/* Sanctions check issues */}
              {selectedVendor.sanctionsCheck && selectedVendor.sanctionsCheck.issues && selectedVendor.sanctionsCheck.issues.length > 0 && <div className="text-sm text-red-600 flex items-start" data-id="element-21510">
                    <ShieldIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-21511" />
                    <div data-id="element-21512">
                      <p className="font-medium" data-id="element-21513">Sanctions Check Issues:</p>
                      <ul className="list-disc list-inside ml-4 mt-1" data-id="element-21514">
                        {selectedVendor.sanctionsCheck.issues.map((issue, index) => <li key={index} className={getSeverityClass(issue.severity)} data-id="element-21515">
                              {issue.description} ({issue.severity} severity)
                            </li>)}
                      </ul>
                      {selectedVendor.sanctionsCheck.lastChecked && <p className="mt-1 text-xs text-gray-500" data-id="element-21516">
                          Last checked:{' '}
                          {selectedVendor.sanctionsCheck.lastChecked}
                        </p>}
                    </div>
                  </div>}
              {/* Pending review status */}
              {selectedVendor.complianceStatus === 'Pending Review' && <div className="text-sm text-yellow-600 flex items-start" data-id="element-21517">
                  <ClockIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-21518" />
                  <div data-id="element-21519">
                    <p className="font-medium" data-id="element-21520">Documents Pending Review:</p>
                    <p className="mt-1" data-id="element-21521">
                      Recently uploaded documents need to be reviewed.
                    </p>
                  </div>
                </div>}
            </div>}
        </div>
      </div>
      {/* Bank Account Verification */}
      {selectedVendor.bankVerification && <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-21522">
          <div className="px-4 py-5 sm:px-6" data-id="element-21523">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-21524">
              Bank Account Verification
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-21525">
              External verification results from Eftsure
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5" data-id="element-21526">
            <div className="flex items-center justify-between mb-4" data-id="element-21527">
              <div className="flex items-center" data-id="element-21528">
                <CreditCardIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-21529" />
                <span className="text-sm font-medium text-gray-900" data-id="element-21530">
                  Verification Status:
                </span>
              </div>
              <div data-id="element-21531">
                {getComplianceBadge(selectedVendor.bankVerification.status)}
              </div>
            </div>
            {selectedVendor.bankVerification.lastChecked && <div className="text-sm text-gray-500 mb-4" data-id="element-21532">
                Last verified: {selectedVendor.bankVerification.lastChecked}
              </div>}
            {selectedVendor.bankVerification.issues && selectedVendor.bankVerification.issues.length > 0 ? <div className="mt-4" data-id="element-21533">
                <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-21534">
                  Verification Issues:
                </h4>
                <ul className="space-y-2" data-id="element-21535">
                  {selectedVendor.bankVerification.issues.map((issue, index) => <li key={index} className={`flex items-start ${getSeverityClass(issue.severity)}`} data-id="element-21536">
                        {getIssueIcon(issue)}
                        <div data-id="element-21537">
                          <p className="font-medium" data-id="element-21538">
                            {issue.severity} Severity:
                          </p>
                          <p data-id="element-21539">{issue.description}</p>
                        </div>
                      </li>)}
                </ul>
              </div> : selectedVendor.bankVerification.status === 'Verified' ? <div className="text-sm text-green-600 flex items-center" data-id="element-21540">
                <CheckCircleIcon className="h-5 w-5 mr-2" data-id="element-21541" />
                Bank account details have been verified successfully with
                Eftsure.
              </div> : <div className="text-sm text-gray-500" data-id="element-21542">
                No verification has been performed yet.
              </div>}
            <div className="mt-4 flex justify-end" data-id="element-21543">
              <button type="button" className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-21544">
                Verify Bank Details
              </button>
            </div>
          </div>
        </div>}
      {/* Sanctions and Watchlist Check */}
      {selectedVendor.sanctionsCheck && <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-21545">
          <div className="px-4 py-5 sm:px-6" data-id="element-21546">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-21547">
              Sanctions & Watchlist Check
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-21548">
              Results from international sanctions and watchlist screening
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5" data-id="element-21549">
            <div className="flex items-center justify-between mb-4" data-id="element-21550">
              <div className="flex items-center" data-id="element-21551">
                <ShieldIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-21552" />
                <span className="text-sm font-medium text-gray-900" data-id="element-21553">
                  Check Status:
                </span>
              </div>
              <div data-id="element-21554">
                {getComplianceBadge(selectedVendor.sanctionsCheck.status)}
              </div>
            </div>
            {selectedVendor.sanctionsCheck.lastChecked && <div className="text-sm text-gray-500 mb-4" data-id="element-21555">
                Last checked: {selectedVendor.sanctionsCheck.lastChecked}
              </div>}
            {selectedVendor.sanctionsCheck.issues && selectedVendor.sanctionsCheck.issues.length > 0 ? <div className="mt-4" data-id="element-21556">
                <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-21557">
                  Detected Issues:
                </h4>
                <ul className="space-y-2" data-id="element-21558">
                  {selectedVendor.sanctionsCheck.issues.map((issue, index) => <li key={index} className={`flex items-start ${getSeverityClass(issue.severity)}`} data-id="element-21559">
                      {getIssueIcon(issue)}
                      <div data-id="element-21560">
                        <p className="font-medium" data-id="element-21561">
                          {issue.severity} Severity:
                        </p>
                        <p data-id="element-21562">{issue.description}</p>
                      </div>
                    </li>)}
                </ul>
              </div> : <div className="text-sm text-green-600 flex items-center" data-id="element-21563">
                <CheckCircleIcon className="h-5 w-5 mr-2" data-id="element-21564" />
                No sanctions or watchlist matches found.
              </div>}
            <div className="mt-4 flex justify-end" data-id="element-21565">
              <button type="button" className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-21566">
                Run New Check
              </button>
            </div>
          </div>
        </div>}
      {/* Documents */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-21567">
        <div className="px-4 py-5 sm:px-6" data-id="element-21568">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-21569">
            Documents
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-21570">
            Review vendor documentation
          </p>
        </div>
        <div className="border-t border-gray-200" data-id="element-21571">
          {selectedVendor.documents && selectedVendor.documents.length > 0 ? <ul className="divide-y divide-gray-200" data-id="element-21572">
              {selectedVendor.documents.map(doc => <li key={doc.id} className="px-4 py-4" data-id="element-21573">
                  <div className="flex items-center justify-between" data-id="element-21574">
                    <div className="flex items-center" data-id="element-21575">
                      <FileTextIcon className="h-5 w-5 text-gray-400 mr-3" data-id="element-21576" />
                      <div data-id="element-21577">
                        <h4 className="text-base font-medium text-gray-900" data-id="element-21578">
                          {doc.name}
                        </h4>
                        <div className="mt-1 flex flex-wrap items-center text-sm text-gray-500" data-id="element-21579">
                          <span className="mr-3" data-id="element-21580">Type: {doc.type}</span>
                          <span className="mr-3" data-id="element-21581">
                            Uploaded: {doc.uploadDate}
                          </span>
                          {doc.expiryDate && <span className="mr-3" data-id="element-21582">
                              <CalendarIcon className="inline-block mr-1 h-4 w-4 text-gray-400" data-id="element-21583" />
                              Expires: {doc.expiryDate}
                            </span>}
                        </div>
                      </div>
                    </div>
                    <div data-id="element-21584">{getDocumentStatusBadge(doc.status)}</div>
                  </div>
                </li>)}
            </ul> : <div className="px-4 py-5 text-center text-sm text-gray-500" data-id="element-21585">
              No documents uploaded yet.
            </div>}
        </div>
      </div>
      {/* Upload New Documents */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-21586">
        <div className="px-4 py-5 sm:px-6" data-id="element-21587">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-21588">
            Upload New Documents
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-21589">
            Add updated compliance documents
          </p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5" data-id="element-21590">
          <FileUpload onFileUpload={handleFileUpload} data-id="element-21591" />
          {files.length > 0 && <div className="mt-4" data-id="element-21592">
              <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-21593">
                New Files to Upload
              </h4>
              <ul className="border rounded-md divide-y divide-gray-200" data-id="element-21594">
                {files.map((file, index) => <li key={index} className="pl-3 pr-4 py-3 flex items-center justify-between text-sm" data-id="element-21595">
                    <div className="flex items-center" data-id="element-21596">
                      <FileTextIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-21597" />
                      <span className="ml-2 flex-1 w-0 truncate" data-id="element-21598">
                        {file.name}
                      </span>
                    </div>
                    <div className="flex items-center" data-id="element-21599">
                      <span className="text-xs text-gray-500 mr-4" data-id="element-21600">
                        {(file.size / 1024).toFixed(1)} KB
                      </span>
                      <button type="button" onClick={() => removeFile(index)} className="text-red-600 hover:text-red-900" data-id="element-21601">
                        <XIcon className="h-4 w-4" data-id="element-21602" />
                      </button>
                    </div>
                  </li>)}
              </ul>
            </div>}
        </div>
      </div>
      {/* Send Message */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-21603">
        <div className="px-4 py-5 sm:px-6" data-id="element-21604">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-21605">
            Send Message to Vendor
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-21606">
            Request updates or provide information
          </p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5" data-id="element-21607">
          <textarea rows={4} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" placeholder="Enter message to vendor..." value={message} onChange={e => setMessage(e.target.value)} data-id="element-21608" />
          <div className="mt-3 flex justify-end" data-id="element-21609">
            <button type="button" onClick={handleSendReminder} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" disabled={!message} data-id="element-21610">
              <SendIcon className="h-4 w-4 mr-1" data-id="element-21611" />
              Send Message
            </button>
          </div>
        </div>
      </div>
      {/* Update Compliance Status */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-21612">
        <div className="px-4 py-5 sm:px-6" data-id="element-21613">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-21614">
            Update Compliance Status
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-21615">
            Change the vendor's compliance status
          </p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5" data-id="element-21616">
          <div className="flex flex-wrap gap-3" data-id="element-21617">
            <button type="button" onClick={() => handleUpdateStatus('Compliant')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" data-id="element-21618">
              <CheckIcon className="h-4 w-4 mr-1" data-id="element-21619" />
              Mark as Compliant
            </button>
            <button type="button" onClick={() => handleUpdateStatus('Pending Review')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500" data-id="element-21620">
              <ClockIcon className="h-4 w-4 mr-1" data-id="element-21621" />
              Mark as Pending Review
            </button>
            <button type="button" onClick={() => handleUpdateStatus('Non-Compliant')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-21622">
              <XIcon className="h-4 w-4 mr-1" data-id="element-21623" />
              Mark as Non-Compliant
            </button>
          </div>
        </div>
      </div>
    </div>;
}